import { Combobox, type ComboboxOption } from '@library/components/selects';
import {
  ENTRY_STATUS_OPTIONS,
  PLAYER_STATUS_OPTIONS
} from '@bo-constants/newWagers';
import type {
  NewWagerSearchFilters,
  NewWagerSearchParam,
  NewWagerSearchParamValue
} from '@library/types/newWagers';
import { useCallback, useMemo, useState } from 'react';
import { PrizepicksDateRange } from '@library/components/datePicker';
import { ResetIconButton } from '@library/components/buttons';
import { Searchbar } from '@library/components/inputs';

export interface MemberEntriesFiltersProps {
  entriesSearchFilters: NewWagerSearchFilters;
  leagueOptions: Array<ComboboxOption>;
  onChangeEntriesFilter: ({ key, value }: NewWagerSearchParam) => void;
  onResetEntriesFilters: () => void;
}

export const MemberEntriesFilters = ({
  entriesSearchFilters,
  leagueOptions,
  onChangeEntriesFilter,
  onResetEntriesFilters
}: MemberEntriesFiltersProps) => {
  const { id, dateRange, entryStatus, leagueId, playerStatus } =
    entriesSearchFilters;

  const { startDate, endDate } = dateRange;
  const formattedStartDate = startDate
    ?.setZone('system', {
      keepLocalTime: true
    })
    .toJSDate();
  const formattedEndDate = endDate?.toJSDate();

  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);

  const leagueNameValue = useMemo(() => {
    return leagueOptions.find(({ value }) => value === leagueId) ?? null;
  }, [leagueId, leagueOptions]);

  const playerStatusValue = useMemo(() => {
    return (
      PLAYER_STATUS_OPTIONS.find(({ value }) => value === playerStatus) ?? null
    );
  }, [playerStatus]);

  const entryStatusValue = useMemo(() => {
    return (
      ENTRY_STATUS_OPTIONS.find(({ value }) => value === entryStatus) ?? null
    );
  }, [entryStatus]);

  const handleEntriesFilterChange = useCallback(
    (key: keyof NewWagerSearchFilters) => (value: NewWagerSearchParamValue) => {
      let filterValue = value;

      if (['entryStatus', 'leagueId', 'playerStatus'].includes(key)) {
        filterValue = (value as ComboboxOption)?.value;
      }

      onChangeEntriesFilter({ key, value: filterValue });
    },
    [onChangeEntriesFilter]
  );

  return (
    <div
      className="flex flex-wrap items-center gap-4"
      data-testid="member-entries-filters"
    >
      <div className="shrink-0 basis-60" data-testid="entries-filter-entry-id">
        <Searchbar
          handleSearch={handleEntriesFilterChange('id')}
          placeholder="Search by Entry ID"
          searchValue={id}
        />
      </div>
      {/* <div className="shrink-0 basis-80">
        <Searchbar
          handleSearch={(value: string) =>
            onChangeEntriesFilter({ key: 'playerName', value })
          }
          placeholder="Search by Player Name"
          searchValue={playerName}
        />
      </div> */}
      <div
        className="shrink-0 basis-[25rem]"
        data-testid="entries-filter-daterange"
      >
        <PrizepicksDateRange
          disabled={false}
          endDate={formattedEndDate}
          endDatePlaceholder="End Date"
          endOpen={endDatePickerOpen}
          handleChange={handleEntriesFilterChange('dateRange')}
          minDate={undefined}
          startOpen={startDatePickerOpen}
          startDate={formattedStartDate}
          startDatePlaceholder="Start Date"
          setEndOpen={setEndDatePickerOpen}
          setStartOpen={setStartDatePickerOpen}
        />
      </div>
      <div className="shrink-0 basis-48" data-testid="entries-filter-league">
        <Combobox
          id="league"
          isMulti={false}
          value={leagueNameValue}
          placeholder="League"
          onChange={handleEntriesFilterChange('leagueId')}
          options={leagueOptions}
          closeMenuOnSelect
          valueClassName="py-[11px]"
        />
      </div>
      <div
        className="shrink-0 basis-48"
        data-testid="entries-filter-entry-status"
      >
        <Combobox
          id="entry-status"
          isMulti={false}
          value={entryStatusValue}
          placeholder="Entry Status"
          onChange={handleEntriesFilterChange('entryStatus')}
          options={ENTRY_STATUS_OPTIONS}
          closeMenuOnSelect
          valueClassName="py-[11px]"
        />
      </div>
      <div
        className="shrink-0 basis-48"
        data-testid="entries-filter-player-status"
      >
        <Combobox
          id="player-status"
          isMulti={false}
          value={playerStatusValue}
          placeholder="Player Status"
          onChange={handleEntriesFilterChange('playerStatus')}
          options={PLAYER_STATUS_OPTIONS}
          closeMenuOnSelect
          valueClassName="py-[11px]"
        />
      </div>
      <ResetIconButton
        className="disabled:text-soClean-140 !m-0 shrink-0 basis-28 !p-0"
        disabled={false}
        label="Reset Filters"
        onClick={onResetEntriesFilters}
      />
    </div>
  );
};
